import React, { useState, useEffect } from 'react';
import axios from 'axios';

const fmt = (n) => n ? Number(n).toLocaleString('tr-TR') : '0';
const fmtTL = (n) => fmt(n) + ' TL';

const AYLAR = ['', 'Oca', 'Sub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Agu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const StatKart = ({ baslik, deger, renk, alt }) => (
  <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1rem 1.25rem' }}>
    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{baslik}</div>
    <div style={{ fontSize: '22px', fontWeight: '700', color: renk || '#111827' }}>{deger}</div>
    {alt && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{alt}</div>}
  </div>
);

const Bolum = ({ baslik, children }) => (
  <div style={{ marginBottom: '2rem' }}>
    <div style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem', paddingBottom: '6px', borderBottom: '1px solid #F3F4F6' }}>
      {baslik}
    </div>
    {children}
  </div>
);

const FinansalRaporuPage = () => {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [seciliYil, setSeciliYil] = useState(new Date().getFullYear());
  const [aktifSekme, setAktifSekme] = useState('donemler');

  const yukle = (yil) => {
    setYukleniyor(true);
    axios.get('/api/finansal-rapor?yil=' + yil)
      .then(r => { setVeri(r.data); setYukleniyor(false); })
      .catch(() => setYukleniyor(false));
  };

  useEffect(() => { yukle(seciliYil); }, [seciliYil]);

  if (yukleniyor) return <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>Yukleniyor...</div>;
  if (!veri) return <div style={{ padding: '2rem', color: '#DC2626' }}>Veri yuklenemedi.</div>;

  const { genel_ozet, donemler, musteri_ozet, kdv_detay, yillar } = veri;

  // Grafik için max değer
  const maxGelir = donemler.length > 0 ? Math.max(...donemler.map(d => d.toplam_gelir || 0)) : 1;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px' }}>Finansal Rapor</h2>
          <p style={{ fontSize: '13px', color: '#6B7280' }}>Konsolide gelir, maliyet ve kar analizi</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select value={seciliYil} onChange={e => setSeciliYil(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
            {(yillar || []).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => (() => { const t = localStorage.getItem('token'); fetch('/api/pdf-rapor/finansal?yil=' + seciliYil, {headers:{Authorization:'Bearer '+t}}).then(r=>r.blob()).then(b=>{const a=document.createElement('a');a.href=URL.createObjectURL(b);a.download='finansal-raporu.pdf';a.click()}); })()}
            style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', background: '#DC2626', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            PDF Indir
          </button>
        </div>
      </div>

      {/* Genel Özet */}
      <Bolum baslik={genel_ozet.yil + ' Yili Finansal Ozet'}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatKart baslik="Toplam Gelir" deger={fmtTL(genel_ozet.toplam_gelir)} renk="#059669" />
          <StatKart baslik="Toplam Maliyet" deger={fmtTL(genel_ozet.toplam_maliyet)} renk="#DC2626" />
          <StatKart baslik="Net Kar" deger={fmtTL(genel_ozet.net_kar)} renk={genel_ozet.net_kar >= 0 ? '#059669' : '#DC2626'} />
          <StatKart baslik="Kar Marji" deger={genel_ozet.kar_marji + '%'} renk={genel_ozet.kar_marji >= 20 ? '#059669' : '#F59E0B'} />
          <StatKart baslik="Aktif Musteri" deger={genel_ozet.musteri_sayisi} renk="#1a56db" />
          <StatKart baslik="Donem Sayisi" deger={genel_ozet.donem_sayisi} renk="#6B7280" />
        </div>

        {/* Grafik */}
        {donemler.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '1rem' }}>Donem Bazli Gelir / Maliyet / Kar</div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', minWidth: donemler.length * 80 + 'px', height: '180px', padding: '0 8px' }}>
                {donemler.map((d, i) => {
                  const gelirH = maxGelir > 0 ? Math.round((d.toplam_gelir / maxGelir) * 150) : 0;
                  const maliyetH = maxGelir > 0 ? Math.round((d.toplam_maliyet / maxGelir) * 150) : 0;
                  const ayStr = d.ay || d.donem || '';
                  const ayNo = ayStr.includes('-') && !ayStr.includes('Q') ? parseInt(ayStr.split('-')[1]) : null;
                  const label = ayNo ? AYLAR[ayNo] : d.donem;
                  return (
                    <div key={i} style={{ flex: 1, minWidth: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                      <div style={{ fontSize: '10px', color: d.net_kar >= 0 ? '#059669' : '#DC2626', fontWeight: '700' }}>
                        {d.net_kar >= 0 ? '+' : ''}{Math.round(d.net_kar / 1000)}K
                      </div>
                      <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: '150px' }}>
                        <div title={'Gelir: ' + fmtTL(d.toplam_gelir)} style={{ width: '18px', height: gelirH + 'px', minHeight: '4px', background: '#059669', borderRadius: '3px 3px 0 0', cursor: 'pointer' }} />
                        <div title={'Maliyet: ' + fmtTL(d.toplam_maliyet)} style={{ width: '18px', height: maliyetH + 'px', minHeight: '4px', background: '#DC2626', borderRadius: '3px 3px 0 0', opacity: 0.7, cursor: 'pointer' }} />
                      </div>
                      <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center' }}>{label}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#059669', borderRadius: '2px', marginRight: '4px' }} />Gelir</span>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#DC2626', borderRadius: '2px', marginRight: '4px', opacity: 0.7 }} />Maliyet</span>
              </div>
            </div>
          </div>
        )}
      </Bolum>

      {/* Sekmeler */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', marginBottom: '1.5rem' }}>
        {[['donemler','Donem Detayi'],['musteriler','Musteri Bazli'],['kdv','KDV Detayi']].map(([k,l]) => (
          <button key={k} onClick={() => setAktifSekme(k)}
            style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: aktifSekme === k ? '600' : '400', color: aktifSekme === k ? '#1a56db' : '#6B7280', borderBottom: aktifSekme === k ? '2px solid #1a56db' : '2px solid transparent' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Dönem Detayı */}
      {aktifSekme === 'donemler' && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
          {donemler.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>Bu yil icin veri bulunamadi</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', color: '#6B7280', fontWeight: '600' }}>Donem</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Gelir</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Maliyet</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Net Kar</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Kar %</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Musteri</th>
                  </tr>
                </thead>
                <tbody>
                  {donemler.map((d, i) => {
                    const ayStr = d.ay || d.donem || '';
                    const ayNo = ayStr.includes('-') && !ayStr.includes('Q') ? parseInt(ayStr.split('-')[1]) : null;
                    const donemLabel = ayNo ? AYLAR[ayNo] + ' ' + ayStr.split('-')[0] : d.donem;
                    const karMarji = d.toplam_gelir > 0 ? Math.round((d.net_kar / d.toplam_gelir) * 100) : 0;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', fontWeight: '500' }}>{donemLabel}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#059669', fontWeight: '600' }}>{fmtTL(d.toplam_gelir)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626' }}>{fmtTL(d.toplam_maliyet)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: d.net_kar >= 0 ? '#059669' : '#DC2626' }}>
                          {d.net_kar >= 0 ? '+' : ''}{fmtTL(d.net_kar)}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <span style={{ background: karMarji >= 20 ? '#D1FAE5' : karMarji >= 0 ? '#FEF3C7' : '#FEE2E2', color: karMarji >= 20 ? '#059669' : karMarji >= 0 ? '#D97706' : '#DC2626', padding: '2px 8px', borderRadius: '99px', fontSize: '12px', fontWeight: '600' }}>
                            {karMarji}%
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{d.musteri_sayisi}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                    <td style={{ padding: '10px 16px', fontWeight: '700' }}>Toplam</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{fmtTL(genel_ozet.toplam_gelir)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: '#DC2626' }}>{fmtTL(genel_ozet.toplam_maliyet)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: genel_ozet.net_kar >= 0 ? '#059669' : '#DC2626' }}>
                      {genel_ozet.net_kar >= 0 ? '+' : ''}{fmtTL(genel_ozet.net_kar)}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <span style={{ background: genel_ozet.kar_marji >= 20 ? '#D1FAE5' : '#FEF3C7', color: genel_ozet.kar_marji >= 20 ? '#059669' : '#D97706', padding: '2px 8px', borderRadius: '99px', fontSize: '12px', fontWeight: '700' }}>
                        {genel_ozet.kar_marji}%
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px' }}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Müşteri Bazlı */}
      {aktifSekme === 'musteriler' && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
          {musteri_ozet.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>Veri bulunamadi</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', color: '#6B7280', fontWeight: '600' }}>Musteri</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Personel</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Gelir</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Maliyet</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Net Kar</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Kar %</th>
                  </tr>
                </thead>
                <tbody>
                  {musteri_ozet.map((m, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 16px', fontWeight: '500' }}>
                        <div>{m.firma_adi}</div>
                        {m.sektor && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{m.sektor}</div>}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ background: '#EEF2FF', color: '#4F46E5', padding: '2px 10px', borderRadius: '99px', fontWeight: '600' }}>{m.personel_sayisi || 0}</span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#059669', fontWeight: '600' }}>{fmtTL(m.toplam_gelir)}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626' }}>{fmtTL(m.toplam_maliyet)}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: m.net_kar >= 0 ? '#059669' : '#DC2626' }}>
                        {m.net_kar >= 0 ? '+' : ''}{fmtTL(m.net_kar)}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ background: m.kar_marji >= 20 ? '#D1FAE5' : m.kar_marji >= 0 ? '#FEF3C7' : '#FEE2E2', color: m.kar_marji >= 20 ? '#059669' : m.kar_marji >= 0 ? '#D97706' : '#DC2626', padding: '2px 8px', borderRadius: '99px', fontSize: '12px', fontWeight: '600' }}>
                          {m.kar_marji}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* KDV Detayı */}
      {aktifSekme === 'kdv' && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
          {kdv_detay.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>KDV verisi bulunamadi</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', color: '#6B7280', fontWeight: '600' }}>Musteri</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Personel</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Fatura (KDV Haric)</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Toplam KDV</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280', fontWeight: '600' }}>Fatura (KDV Dahil)</th>
                  </tr>
                </thead>
                <tbody>
                  {kdv_detay.map((k, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={{ padding: '10px 16px', fontWeight: '500' }}>{k.firma_adi}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <span style={{ background: '#EEF2FF', color: '#4F46E5', padding: '2px 10px', borderRadius: '99px', fontWeight: '600' }}>{k.personel_sayisi}</span>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>{fmtTL(k.fatura_haric_kdv)}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#F59E0B', fontWeight: '600' }}>{fmtTL(Math.round(k.toplam_kdv))}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#059669', fontWeight: '700' }}>{fmtTL(Math.round(k.fatura_dahil_kdv))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                    <td style={{ padding: '10px 16px', fontWeight: '700' }}>Toplam</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700' }}>{kdv_detay.reduce((t, k) => t + k.personel_sayisi, 0)}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700' }}>{fmtTL(kdv_detay.reduce((t, k) => t + (k.fatura_haric_kdv || 0), 0))}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: '#F59E0B' }}>{fmtTL(Math.round(kdv_detay.reduce((t, k) => t + (k.toplam_kdv || 0), 0)))}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{fmtTL(Math.round(kdv_detay.reduce((t, k) => t + (k.fatura_dahil_kdv || 0), 0)))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinansalRaporuPage;
